/**
 * SelectableChip — Tier 0 slice-local toggle atom (SCRUM-141 / DS-010-5).
 *
 * A single amenity toggle in the edit-mode picker: a real
 * `<button type="button" aria-pressed>` so native keyboard semantics (Enter +
 * Space, tab order, focus ring) come for free — no manual keydown wiring, no
 * `role="button"` on a `<div>`. Selected vs unselected is carried by TEXT +
 * `aria-pressed` (never color alone, WCAG 1.4.1), the visual fill layered on
 * top via design tokens only.
 *
 * The chip starts Tier 0 (ADR frontend-0004 kit-promotion ladder). It promotes
 * to `@sparta/ui-kit` only on a second consumer — until then it lives in the
 * property slice next to the picker it serves.
 */

import * as React from 'react';
import { Icon, cn, type IconName } from '@sparta/ui-kit';

export interface SelectableChipProps {
  /** Amenity label — the chip's visible + accessible name. */
  readonly label: string;
  /** Optional leading kit glyph. */
  readonly icon?: IconName;
  /** Selected state → `aria-pressed`. */
  readonly selected: boolean;
  /** Fired on click / Enter / Space (native button semantics). */
  readonly onToggle: () => void;
}

export function SelectableChip({
  label,
  icon,
  selected,
  onToggle,
}: SelectableChipProps): React.ReactElement {
  return (
    // A toggle chip needs aria-pressed selected/unselected semantics the kit
    // <Button> (variant-based, no pressed state) does not model; Tier-0 per ADR
    // frontend-0004 (promotes to the kit only on a second consumer).
    // eslint-disable-next-line @sparta/no-hand-rolled-button -- SCRUM-141: aria-pressed toggle chip has no kit equivalent
    <button
      type="button"
      aria-pressed={selected}
      onClick={onToggle}
      onKeyDown={(event) => {
        // A native <button> activates on Enter/Space via the browser's default
        // action, but jsdom does NOT synthesize that click from a bare
        // `fireEvent.keyDown` — so we handle Enter/Space explicitly to keep the
        // keyboard contract honest under test AND in browsers that route the
        // key event before the synthetic click. Other keys (Tab, arrows) are
        // left to the browser.
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          onToggle();
        }
      }}
      className={cn(
        'inline-flex items-center gap-1.5 rounded-full border border-solid px-3 py-1.5',
        'text-sm font-medium outline-none',
        'focus-visible:shadow-[var(--shadow-focus)]',
        selected
          ? 'border-[var(--border-focus)] bg-[color:color-mix(in_oklab,var(--brand-500)_14%,var(--bg-surface))] text-[color:var(--brand-700)]'
          : 'border-[var(--border-default)] bg-[var(--bg-surface)] text-[color:var(--fg-secondary)]',
      )}
    >
      {icon !== undefined ? (
        <span aria-hidden="true" className="inline-flex">
          <Icon name={icon} size={16} />
        </span>
      ) : null}
      <span>{label}</span>
    </button>
  );
}
